import type { OutboxDocumentWrite } from '@colplat/core-client';
import type { PresenceUser } from '@colplat/protocol';

/**
 * Reconciling unsent work with server state, for display.
 *
 * One rule, applied everywhere: **while work is unsent, the outbox is the
 * more recent truth for that record.** Anything rendering it prefers the
 * queued version; anything writing it carries the queued `baseVersion`
 * rather than re-reading a fresher one, because that is what makes a genuine
 * conflict detectable instead of silently resolved (ADR-0011).
 *
 * Extracted from `webledgerTransactions.ts` because getting this wrong is
 * invisible rather than loud — the two bugs it caused (an offline edit that
 * did not appear in the list, and a reopened transaction showing its pre-edit
 * values) both looked like nothing happening. Pure functions so the failure
 * modes are unit-testable without a DOM, a modal or a socket.
 */

/**
 * Reference data every webledger page formats against. Here rather than in
 * any one view because the register, the reports and the editor all need
 * them, and three structurally identical interfaces in three files is how
 * they end up quietly disagreeing.
 */
export interface AccountDto {
  id: string; parentId: string | null; name: string; type: string; currencyCode: string;
  isPlaceholder: boolean; isMulticurrency: boolean;
  sortOrder?: number;
  description?: string;
  /** Bare filename of the seeded icon, e.g. `000-assets.svg`. Empty when none. */
  iconPath?: string;
  /** Per-currency balance of this account's own splits, not rolled up. Decimal text. */
  balances?: Record<string, string>;
  purposeIds?: string[];
}
export interface CurrencyDto {
  id: string; code: string; name: string; symbol: string; fraction: number; useForTotals: boolean;
}

export interface SplitDto {
  id: string; transactionId: string; accountId: string; memo: string;
  value: string; amount: string; currencyCode: string; version: number;
  /** ADR-0023. Null on category legs, and on money legs nobody has earmarked yet. */
  walletId?: string | null;
}

export interface TransactionDto {
  id: string; txDate: string; description: string; reference: string;
  currencyCode: string; version: number; splits: SplitDto[];
  /**
   * Who holds it, from its hold (ADR-0003, 2026-10-22): `ownedBy` whenever
   * somebody does, connected or not — "X started editing this 2 hours ago
   * and hasn't finished"; `lockedBy` only while their tab is connected — "X is
   * editing this now". Kept live afterwards by `leaseTaken`/`leaseFreed`.
   */
  lockedBy?: PresenceUser | null;
  ownedBy?: PresenceUser | null;
  /** ISO-8601 timestamp for "started editing 6 minutes ago". */
  ownedSince?: string | null;
  /** Set on anything sourced from the offline outbox — not yet on the server. */
  pendingSync?: boolean;
}

/** The save body `queueOffline` stores in the outbox — the request, held for later. */
export interface QueuedTransactionPayload {
  id: string; txDate: string; reference?: string; description?: string; currencyCode: string;
  baseVersion?: number;
  splits: Array<{
    id: string; op?: string; baseVersion?: number; accountId: string;
    memo?: string; value: string; amount: string; currencyCode: string;
    /** ADR-0023 — carried through, or an entry re-opened offline would come back un-earmarked. */
    walletId?: string | null;
  }>;
  /** Tag changes, as members of this same save: `create` with a tag, or `delete` by row id. */
  tags?: Array<{ op?: string; id: string; tagId?: string }>;
  /**
   * Why money crossed between two wallets, when it did (ADR-0023).
   *
   * Rides in the transaction's own commit so the legs and the fact about
   * them land together or not at all — see `walletMoveMembers`. Absent means
   * "this save has nothing to say about it"; a present record with an empty
   * `kind` means "there is no longer one of these".
   *
   * The short form says one of these and the full editor says a list, because
   * an entry with four legs can carry two of them. `amountSplitId` is which
   * leg the move was — the question a from/to pair could not answer once
   * there was more than one leg it might have meant.
   */
  walletMove?: {
    id: string; kind: string; fromWalletId: string; toWalletId: string;
    amountSplitId?: string; amount?: string | null;
  };
  walletMoves?: Array<{
    id: string; kind: string; fromWalletId: string; toWalletId: string; amountSplitId: string;
    amount?: string | null;
  }>;
}

/**
 * A queued payload rendered as the transaction it will become, so the list
 * and the form can show the user their own pending work.
 *
 * `version` comes from the payload's `baseVersion`, not from anything current:
 * re-saving must keep comparing against the base the first edit was made on,
 * or a conflict that happened in between would go undetected.
 */
export function queuedPayloadToDto(payload: unknown): TransactionDto {
  const p = payload as QueuedTransactionPayload;
  return {
    id: p.id,
    txDate: p.txDate,
    description: p.description ?? '',
    reference: p.reference ?? '',
    currencyCode: p.currencyCode,
    version: p.baseVersion ?? 0,
    pendingSync: true,
    // A removal is not a row to render; it is the absence of one.
    splits: p.splits
      .filter((sp) => sp.op !== 'delete')
      .map((sp) => ({
        id: sp.id, transactionId: p.id, accountId: sp.accountId, memo: sp.memo ?? '',
        value: sp.value, amount: sp.amount, currencyCode: sp.currencyCode,
        version: sp.baseVersion ?? 0,
        // Dropped before 2026-10-21: an entry edited offline, opened again
        // offline and saved, lost every earmark on the second save.
        walletId: sp.walletId ?? null,
      })),
  };
}

/** What the replica holds about one transaction, as the server sent it. */
export interface ServerCopy {
  transaction: (id: string) => (Record<string, unknown> & { id: string }) | undefined;
  tagsOf: (transactionId: string) => ReadonlyArray<{ id: string; transactionId: string; tagId: string }>;
  transfersOf: (transactionId: string) => ReadonlyArray<Record<string, unknown> & { id: string }>;
}

/**
 * A large `seq`, so a transaction created offline sorts last within its day —
 * where the server will put it (`nextSeq`) when it arrives.
 */
const LAST_OF_DAY = Number.MAX_SAFE_INTEGER;

/**
 * Unsent creates and edits, as the replica rows they will become
 * (`Dataset.setPendingRows`, ADR-0011 amendment of 2026-10-21).
 *
 * One function for every view: the wallet log, the totals, the accounts and
 * reports all read the replica, so laying the queued work over it here is
 * what makes an entry saved offline appear everywhere at once instead of
 * nowhere until the connection returns.
 *
 * Only work on its way to the server: a held draft is a proposal its owner
 * has not chosen (it would push over the person who took the record), and
 * deletes and restores already have their own overlay.
 *
 * Children follow the payload's own rules, which are the server's:
 *  - tags are changes — `create` adds one, `delete` takes one off;
 *  - `walletMoves` is the whole list; `walletMove` is one record relabelled
 *    onto whatever the entry already had (`walletMoveMembers`), and an empty
 *    `kind` means there is none any more; absent means "unchanged".
 */
export function pendingRowsFromOutbox(
  entries: readonly OutboxDocumentWrite[], server: ServerCopy,
): Map<string, Map<string, (Record<string, unknown> & { id: string }) | null>> {
  const transactions = new Map<string, (Record<string, unknown> & { id: string }) | null>();
  const tags = new Map<string, (Record<string, unknown> & { id: string }) | null>();
  const transfers = new Map<string, (Record<string, unknown> & { id: string }) | null>();

  for (const q of entries) {
    if (q.entity !== 'transaction' || q.held || (q.op !== 'create' && q.op !== 'update')) continue;
    const p = q.payload as QueuedTransactionPayload;
    const before = q.op === 'update' ? server.transaction(p.id) : undefined;
    transactions.set(p.id, {
      ...(before ?? { seq: LAST_OF_DAY, version: 0 }),
      // The version the edit was MADE on, not the server's current one:
      // opening it again and saving must still compare against that base,
      // or a change somebody made in between would be overwritten unseen.
      ...(q.op === 'update' && p.baseVersion !== undefined ? { version: p.baseVersion } : {}),
      id: p.id,
      txDate: p.txDate,
      description: p.description ?? '',
      reference: p.reference ?? '',
      currencyCode: p.currencyCode,
      deleted_at: null,
      legs: p.splits.filter((sp) => sp.op !== 'delete').map((sp) => ({
        id: sp.id, accountId: sp.accountId, memo: sp.memo ?? '',
        value: sp.value, amount: sp.amount, currencyCode: sp.currencyCode, walletId: sp.walletId ?? null,
      })),
    });

    for (const t of p.tags ?? []) {
      if (t.op === 'delete') tags.set(t.id, null);
      else if (t.tagId) tags.set(t.id, { id: t.id, transactionId: p.id, tagId: t.tagId });
    }

    const had = q.op === 'update' ? server.transfersOf(p.id) : [];
    const row = (m: NonNullable<QueuedTransactionPayload['walletMove']>, id: string) => ({
      id, transactionId: p.id, kind: m.kind, fromWalletId: m.fromWalletId, toWalletId: m.toWalletId,
      amountSplitId: m.amountSplitId ?? null, amount: m.amount ?? null,
    });
    if (p.walletMoves) {
      const keep = new Set(p.walletMoves.map((m) => m.id));
      for (const old of had) if (!keep.has(old.id)) transfers.set(old.id, null);
      for (const m of p.walletMoves) transfers.set(m.id, row(m, m.id));
    } else if (p.walletMove) {
      if (p.walletMove.kind === '') {
        for (const old of had) transfers.set(old.id, null);
      } else {
        // Relabelled onto the record already there, as the server does.
        const id = had[0]?.id ?? p.walletMove.id;
        for (const old of had) if (old.id !== id) transfers.set(old.id, null);
        transfers.set(id, row(p.walletMove, id));
      }
    }
  }

  const out = new Map<string, Map<string, (Record<string, unknown> & { id: string }) | null>>();
  if (transactions.size > 0) out.set('transaction', transactions);
  if (tags.size > 0) out.set('transactionTag', tags);
  if (transfers.size > 0) out.set('walletTransfer', transfers);
  return out;
}

/**
 * An edit of an entry that has itself not been sent yet, folded into its
 * queued create.
 *
 * Queued as an `update` it would replace the create in the outbox
 * (`enqueueDocument` keeps one entry per record) and reach the server as a
 * change to a record that does not exist. So it stays a create: every leg a
 * new one, the tags the create had with this edit's changes applied, and the
 * edit's own word on the debt between wallets.
 */
export function foldIntoQueuedCreate(
  created: QueuedTransactionPayload, edit: QueuedTransactionPayload,
): QueuedTransactionPayload {
  const tagIds = new Map<string, string>();
  for (const t of created.tags ?? []) if (t.op !== 'delete' && t.tagId) tagIds.set(t.id, t.tagId);
  for (const t of edit.tags ?? []) {
    if (t.op === 'delete') tagIds.delete(t.id);
    else if (t.tagId) tagIds.set(t.id, t.tagId);
  }
  const { baseVersion: _base, ...rest } = edit;
  void _base;
  const folded: QueuedTransactionPayload = {
    ...rest,
    splits: edit.splits.filter((sp) => sp.op !== 'delete').map(({ baseVersion: _b, ...sp }) => {
      void _b;
      return { ...sp, op: 'create' };
    }),
    tags: [...tagIds].map(([id, tagId]) => ({ op: 'create', id, tagId })),
  };
  // Neither said anything about a debt: the create's stands.
  if (!edit.walletMove && !edit.walletMoves) {
    if (created.walletMove) folded.walletMove = created.walletMove;
    if (created.walletMoves) folded.walletMoves = created.walletMoves;
  }
  return folded;
}

/**
 * The queued edit for one record, if the outbox is holding one.
 *
 * Held entries are excluded on purpose. A queued edit is work the user
 * expects to land and so outranks the server copy everywhere; a *held* one
 * is a candidate they have not chosen yet, and quietly loading it as the
 * form's content would re-apply a draft over the person who took the record
 * — the exact move the held state exists to prevent. It is offered beside
 * the current values instead (`heldFor`).
 */
export function queuedUpdateFor(
  entries: readonly OutboxDocumentWrite[], recordId: string,
): OutboxDocumentWrite | undefined {
  return entries.find(
    (q) => q.entity === 'transaction' && q.op === 'update' && q.recordId === recordId && !q.held,
  );
}

/** The interrupted draft held for one record, if there is one. */
export function heldFor(
  entries: readonly OutboxDocumentWrite[], recordId: string,
): OutboxDocumentWrite | undefined {
  return entries.find(
    (q) => q.entity === 'transaction' && q.recordId === recordId && q.held !== undefined,
  );
}

/** Held drafts by record id, for the list's marker. */
export function heldRows(
  entries: readonly OutboxDocumentWrite[],
): Map<string, OutboxDocumentWrite> {
  return new Map(
    entries.filter((q) => q.entity === 'transaction' && q.held !== undefined).map((q) => [q.recordId, q]),
  );
}

/** Queued creates, as rows the list can render, in outbox (HLC) order. */
export function queuedCreateRows(entries: readonly OutboxDocumentWrite[]): TransactionDto[] {
  return entries
    .filter((q) => q.entity === 'transaction' && q.op === 'create' && !q.held)
    .map((q) => queuedPayloadToDto(q.payload));
}

/** Queued edits of existing records, keyed by record id. */
export function queuedUpdateRows(
  entries: readonly OutboxDocumentWrite[],
): Map<string, TransactionDto> {
  return new Map(
    entries
      .filter((q) => q.entity === 'transaction' && q.op === 'update' && !q.held)
      .map((q) => [q.recordId, queuedPayloadToDto(q.payload)]),
  );
}

/**
 * The rows the transaction list should render.
 *
 * Queued creates come first — they are the newest thing the user did and the
 * ones needing attention — and a queued edit is *substituted* for its server
 * row rather than shown beside it. Rendering the server row with a "waiting
 * to sync" badge next to it was accurate about the record and useless to the
 * user: their edit looked like it had done nothing at all.
 */
export function listRowsFor(
  pendingCreates: readonly TransactionDto[],
  transactions: readonly TransactionDto[],
  pendingUpdates: ReadonlyMap<string, TransactionDto>,
): TransactionDto[] {
  return [
    ...pendingCreates,
    ...transactions.map((t) => pendingUpdates.get(t.id) ?? t),
  ];
}

/**
 * Which version of a record to put in the editor.
 *
 * `serverCopy` is whatever we could reach — a live read, the per-record
 * cache, or the list entry — and none of those know about an edit made since.
 * So an unsent edit wins. This is the decision that, inverted, showed
 * pre-edit values in the form and then overwrote the queued version with them
 * on the next save.
 */
export function documentToEdit(
  serverCopy: TransactionDto,
  queuedEdit: OutboxDocumentWrite | undefined,
): { tx: TransactionDto; fromOutbox: boolean } {
  if (!queuedEdit) return { tx: serverCopy, fromOutbox: false };
  return { tx: queuedPayloadToDto(queuedEdit.payload), fromOutbox: true };
}

/**
 * One place where a held draft differs from what is on the record now.
 *
 * `where` says which control to hang the marker on: a transaction-level field
 * name, or `split:<id>` for a leg. Splits are compared whole rather than by
 * sub-field because a leg only means anything complete — an account without
 * its amount is not a thing anybody can decide about — and because a
 * transaction has to balance, so offering "keep my memo but their amount"
 * would invite an unbalanced entry.
 */
export interface HeldDifference {
  where: string;
  label: string;
  /** Your value, formatted for display. */
  mine: string;
}

function splitLine(sp: QueuedTransactionPayload['splits'][number], accountName: (id: string) => string): string {
  const value = Number(sp.value);
  const side = value >= 0 ? '+' : '−';
  const parts = [`${accountName(sp.accountId)} ${side}${Math.abs(value)}`];
  if (sp.memo) parts.push(sp.memo);
  return parts.join(' · ');
}

/**
 * What you would lose, as a list.
 *
 * Deliberately one-sided: it reports *your* value, not a before/after pair.
 * The other half is already on screen in the control the marker sits under,
 * live and still changing as its new holder types — restating it beside a
 * frozen copy would be one more thing to read and one more thing to go out
 * of date.
 */
export function heldDifferences(
  held: QueuedTransactionPayload,
  current: QueuedTransactionPayload,
  accountName: (id: string) => string,
): HeldDifference[] {
  const out: HeldDifference[] = [];
  const field = (where: string, label: string, mine: string, theirs: string): void => {
    if (mine !== theirs) out.push({ where, label, mine: mine === '' ? '(empty)' : mine });
  };
  field('txDate', 'Date', held.txDate, current.txDate);
  field('reference', 'Reference', held.reference ?? '', current.reference ?? '');
  field('description', 'Description', held.description ?? '', current.description ?? '');
  field('currencyCode', 'Currency', held.currencyCode, current.currencyCode);

  const currentSplits = new Map(current.splits.map((sp) => [sp.id, sp]));
  for (const mine of held.splits) {
    if (mine.op === 'delete') {
      // A leg you removed that is still there. Worth saying: it is the one
      // difference invisible in the form, since nothing is rendered where
      // your version has nothing.
      if (currentSplits.has(mine.id)) {
        out.push({ where: `split:${mine.id}`, label: 'Split', mine: 'removed by you' });
      }
      continue;
    }
    const theirs = currentSplits.get(mine.id);
    if (!theirs) {
      out.push({ where: `split:${mine.id}`, label: 'Split you added', mine: splitLine(mine, accountName) });
      continue;
    }
    const same = mine.accountId === theirs.accountId && (mine.memo ?? '') === (theirs.memo ?? '')
      && Number(mine.value) === Number(theirs.value) && Number(mine.amount) === Number(theirs.amount)
      && mine.currencyCode === theirs.currencyCode;
    if (!same) out.push({ where: `split:${mine.id}`, label: 'Split', mine: splitLine(mine, accountName) });
  }
  return out;
}

// ------------------------------------------------- deleting without a connection

/**
 * Deleting offline, and the two quite different situations it covers.
 *
 * **A record that has never reached the server.** Created offline, still
 * sitting in the outbox as a `create`. Deleting it is not a delete at all —
 * nobody has ever seen this record, there is nothing to tell anyone about, and
 * the right answer is to drop the queued create and be done. Queueing a delete
 * for a record the server has never heard of would get a 404 and need special
 * handling to recognise that the 404 was the desired outcome.
 *
 * **A record the server already has.** This one is a real delete, queued with
 * the `baseVersion` it was deleted at, and hidden locally until the server
 * agrees. If the record moved on while the device was away the server refuses
 * it, and the record comes back — see `webledgerTransactions.ts`'s flush. That
 * is the conservative direction: an offline delete applied blind would erase
 * edits made in between, and the person who made them would never learn why.
 */
export type OfflineDeletePlan =
  | { kind: 'drop-unsent-create'; outboxId: string }
  | { kind: 'queue-delete'; baseVersion: number };

export function planOfflineDelete(
  queued: readonly OutboxDocumentWrite[],
  recordId: string,
  serverVersion: number,
): OfflineDeletePlan {
  const unsentCreate = queued.find(
    (q) => q.recordId === recordId && q.op === 'create' && !q.held,
  );
  if (unsentCreate) return { kind: 'drop-unsent-create', outboxId: unsentCreate.id };

  /*
   * A queued *edit* carries the base it was made against, and that is the one
   * to delete against too. Re-reading a fresher version here would paper over
   * exactly the conflict this is meant to surface — the edit and the delete
   * were both decided while looking at the same copy.
   */
  const queuedEdit = queued.find((q) => q.recordId === recordId && q.op === 'update' && !q.held);
  const base = queuedEdit ? baseVersionOf(queuedEdit.payload) : undefined;
  return { kind: 'queue-delete', baseVersion: base ?? serverVersion };
}

function baseVersionOf(payload: unknown): number | undefined {
  const v = (payload as { baseVersion?: unknown } | null)?.baseVersion;
  return typeof v === 'number' ? v : undefined;
}

/**
 * Which records each entity is hiding because of an unsent delete.
 *
 * Derived from the outbox every time rather than stored: the queued delete is
 * the fact, and a second copy of it is a second thing to keep right. Handed
 * to `Dataset.setPendingDeletes`, which is what makes every list agree at
 * once.
 */
export function pendingDeletesByEntity(
  queued: readonly OutboxDocumentWrite[],
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const q of queued) {
    if (q.op !== 'delete' || q.held) continue;
    let bag = out.get(q.entity);
    if (!bag) { bag = new Set(); out.set(q.entity, bag); }
    bag.add(q.recordId);
  }
  return out;
}

/** The queued delete for a record, if one is waiting to be sent. */
export function queuedDeleteFor(
  queued: readonly OutboxDocumentWrite[],
  recordId: string,
): OutboxDocumentWrite | undefined {
  return queued.find((q) => q.recordId === recordId && q.op === 'delete' && !q.held);
}

/**
 * Records restored here and not yet accepted by the server.
 *
 * The mirror of `pendingDeletesByEntity`, and derived the same way: the queued
 * restore is the fact, and the dataset is told so every list shows the record
 * again at once rather than only the page it was restored from.
 */
export function pendingRestoresByEntity(
  queued: readonly OutboxDocumentWrite[],
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const q of queued) {
    if (q.op !== 'restore' || q.held) continue;
    let bag = out.get(q.entity);
    if (!bag) { bag = new Set(); out.set(q.entity, bag); }
    bag.add(q.recordId);
  }
  return out;
}

/**
 * What pressing "Put back" should actually do.
 *
 * Undoing a delete that never left this device is not a restore — the server
 * still has the record as live and has never heard otherwise, so sending it a
 * restore would be asking it to undo something it never did. Dropping the
 * queued delete is the whole of it, and it is the only one of the two that
 * works with no connection *and* no server round trip.
 */
export type RestorePlan =
  | { kind: 'cancel-unsent-delete'; outboxId: string }
  | { kind: 'queue-restore' };

export function planRestore(
  queued: readonly OutboxDocumentWrite[],
  recordId: string,
): RestorePlan {
  const unsent = queuedDeleteFor(queued, recordId);
  return unsent
    ? { kind: 'cancel-unsent-delete', outboxId: unsent.id }
    : { kind: 'queue-restore' };
}
